import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { MediaObserver } from '@angular/flex-layout';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { ChangeContext, Options, PointerType } from '@angular-slider/ngx-slider';

import { PriceFilterRange, PriceRangeLabel } from '~shared/models';
import * as fromFilters from '~app/modules/filters/store';
import { PriceRange } from '~app/modules/filters/models';
import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
import { PlpPriceFilterInteractionEvent } from '~shared/modules/application-event/models';
import { ApplicationEventPriceFilterInteractionType } from '~shared/modules/application-event/constants';
import { MEDIA_ALISES } from '~shared/constants/media.constant';
import { generatePriceFilterNodes } from './price-filter-nodes-generator';
import { NUMBER_0, NUMBER_60000 } from '~shared/constants';

@Component({
  selector: 'app-search-price-filter',
  templateUrl: './price-filter.component.html',
  styleUrls: ['./price-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PriceFilterComponent implements OnInit, OnDestroy {
  @Output()
  priceFilterChanged = new EventEmitter<PriceFilterRange>();

  options: Options = {
    minRange: 1,
    pushRange: true,
    animate: false,
    hidePointerLabels: true,
    hideLimitLabels: true,
    stepsArray: [],
    enforceStep: false,
    enforceStepsArray: false,
  };

  min: number;
  max: number;
  from: number;
  to: number;

  lastAppliedPriceRange: PriceFilterRange = null;
  isMobileOrTablet: boolean;

  private unsubscribe$ = new Subject();

  constructor(
    private mediaObserver: MediaObserver,
    private store: Store<fromFilters.State>,
    private cd: ChangeDetectorRef,
    private applicationEventService: ApplicationEventService
  ) {}

  ngOnInit(): void {
    this.isMobileOrTablet =
      this.mediaObserver.isActive(MEDIA_ALISES.mobile) || this.mediaObserver.isActive(MEDIA_ALISES.tablet);

    this.store
      .select(fromFilters.getPriceFilters)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((priceRange: PriceFilterRange) => {
        this.onPriceRangeStoreChange(priceRange);
      });

    this.store
      .select(fromFilters.getPriceRange)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((minMaxRange: PriceRange) => {
        this.onMinMaxStoreChange(minMaxRange);
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onFromChange(event: Event): void {
    let from = (event.target as any).valueAsNumber;
    from = Math.min(Number(this.to) - 1, Math.max(from, this.min));
    this.from = isNaN(from) ? this.min : from;
    this.firePriceFilterChangeEvent(PriceRangeLabel.min);
    this.firePriceFilterInteractionEvent(ApplicationEventPriceFilterInteractionType.InputMin);
  }

  onToChange(event: Event) {
    let to = (event.target as any).valueAsNumber;
    to = Math.min(this.max, Math.max(to, Number(this.from) + 1));
    this.to = isNaN(to) ? this.max : to;
    this.firePriceFilterChangeEvent(PriceRangeLabel.max);
    this.firePriceFilterInteractionEvent(ApplicationEventPriceFilterInteractionType.InputMax);
  }

  onUserChangeEnd(changeContext: ChangeContext) {
    this.firePriceFilterChangeEvent(changeContext.pointerType ? PriceRangeLabel.max : PriceRangeLabel.min);

    this.firePriceFilterInteractionEvent(
      changeContext.pointerType === PointerType.Min
        ? ApplicationEventPriceFilterInteractionType.SliderMin
        : ApplicationEventPriceFilterInteractionType.SliderMax
    );
  }

  private onPriceRangeStoreChange(priceRange: PriceFilterRange): void {
    if (!priceRange || priceRange.from == null || priceRange.to == null) {
      this.from = this.min;
      this.to = this.max;
      this.lastAppliedPriceRange = null;
    } else {
      this.lastAppliedPriceRange = { ...priceRange };
      this.from = priceRange.from;
      this.to = priceRange.to;
    }

    const newOptions = { ...this.options };
    newOptions.stepsArray = generatePriceFilterNodes(this.min, this.max);
    this.options = newOptions;

    this.cd.markForCheck();
  }

  private onMinMaxStoreChange(minMaxRange: PriceRange): void {
    if (!minMaxRange || minMaxRange.min == null || minMaxRange.max == null) {
      return;
    }

    if (minMaxRange.min === this.min && minMaxRange.max === this.max) {
      return;
    }

    this.min = minMaxRange.min;
    this.max = minMaxRange.max;

    const newOptions = { ...this.options };
    newOptions.stepsArray = generatePriceFilterNodes(this.min, this.max);
    this.options = newOptions;

    if (this.lastAppliedPriceRange) {
      this.from = Math.max(this.lastAppliedPriceRange.from, this.min);
      this.to = Math.min(this.lastAppliedPriceRange.to, this.max);
    } else {
      this.from = this.min;
      this.to = this.max;
    }
    this.cd.markForCheck();
  }

  private firePriceFilterChangeEvent(type: PriceRangeLabel): void {
    this.priceFilterChanged.emit({ from: Number(this.from) || NUMBER_0, to: Number(this.to) || NUMBER_60000, type });
  }

  private firePriceFilterInteractionEvent(type: ApplicationEventPriceFilterInteractionType): void {
    this.applicationEventService.publish(new PlpPriceFilterInteractionEvent(type));
  }

  protected readonly parseInt = parseInt;
}
